import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

// Hooks
import useBalance from "./useBalance";

// Material-UI Components
import { Box, ClickAwayListener, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

// Components
import PersonWorkHistory from "components/PersonWorkHistory";
import CustomiseLabels from "components/Charts/components/CustomiseLabelsLegacy";
import { ChartErrorModal } from "components/Charts/components/ChartErrorModal/ChartErrorModal";

// Types
import { IBaseChartProps } from "components/Charts/chart.types";

// Styles
import "assets/styles/components/highcharts.css";

// Images
import { CROSS_HAIR_CURSOR } from "../chart.constants";

window.Highcharts = Highcharts;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            minHeight: "100vh",
            width: "100%",
            zIndex: 1201,
        },
        errorLegend: {
            display: "flex",
            position: "absolute",
            bottom: -40,
            padding: "5px",
            color: theme.palette.warning.main,
            zIndex: 10,
            alignItems: "center",
        },
        infoIcon: {
            transform: "rotate(180deg)",
            cursor: "pointer !important",
            color: theme.palette.warning.main,
            zIndex: 10,
        },
    })
);

const Balance = ({
    width,
    height,
    getData,
    getbullEyeCoors,
    fromRefinement,
    moreOptions,
}: IBaseChartProps): JSX.Element => {
    const classes = useStyles();

    const [
        {
            chartOptions,
            chartData,
            chartRef,
            showNoDataModal,
            chartCircles,
            personDataElement,
            peopleDataElement,
            containerRef,
            showRoles,
            showNames,
        },
        {
            onClose,
            setShowRoles,
            setShowNames,
            showAllLabels,
            setShowAllLabels,
            hideAllLabels,
            setHideAllLabels,
            handleToggleArchetype,
            onCloseNoDataModal,
        },
    ] = useBalance({ width, height, getData, getbullEyeCoors, fromRefinement, moreOptions });

    return (
        <div
            id="container"
            style={{
                width: "100%",
                height: "100%",
                margin: "auto",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
            ref={containerRef}
        >
            {(personDataElement.display || peopleDataElement) &&
            (personDataElement.personId || peopleDataElement?.peopleIds?.length > 0) ? (
                <>
                    <ClickAwayListener onClickAway={onClose}>
                        <PersonWorkHistory
                            personId={personDataElement.personId}
                            peopleIds={peopleDataElement.peopleIds}
                            role={personDataElement.role}
                            onClose={onClose}
                            parentWidth={containerRef?.current?.clientWidth}
                            parentHeight={containerRef?.current?.clientHeight}
                            xElement={peopleDataElement.x || personDataElement.x}
                            yElement={peopleDataElement.y || personDataElement.y}
                            isBalanceChart={true}
                            isChart={true}
                        />
                    </ClickAwayListener>
                    <Box className={classes.overlay}></Box>
                </>
            ) : null}

            {chartData && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{
                        width: "100%",
                        height: "100%",
                        margin: "auto",
                        position: "relative",
                        cursor: moreOptions?.bullEyeCoors ? CROSS_HAIR_CURSOR : "auto",
                    }}
                >
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                </Box>
            )}

            <ChartErrorModal open={showNoDataModal} onClose={onCloseNoDataModal} containerRef={containerRef} />

            {!moreOptions?.hideCustomizeLabels && (
                <CustomiseLabels
                    setShowRoles={setShowRoles}
                    showRoles={showRoles}
                    setShowNames={setShowNames}
                    showNames={showNames}
                    isSwitchAllLabelsVisible
                    showAllLabels={showAllLabels}
                    setShowAllLabels={setShowAllLabels}
                    hideAllLabels={hideAllLabels}
                    setHideAllLabels={setHideAllLabels}
                    chartCircles={chartCircles}
                    handleToggleArchetype={handleToggleArchetype}
                />
            )}
        </div>
    );
};

export default Balance;
